import React, {useCallback, useEffect, useState} from 'react'
import {Dialog, ToggleSwitch} from '@primer/react'
import styles from './CustomizationDialog.module.css'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {COLUMN_BAR_CHART_TYPES} from './ChartByType/shared'

export const CHART_CARD_CUSTOMIZATION_SETTINGS = {
  chartCardIncreasedContrast: 'chart-card-increased-contrast',
  chartCardHasLineStyle: 'chart-card-line-style',
  chartCardLineDataLabels: 'chart-card-line-data-labels',
  chartCardColumnDataLabels: 'chart-card-column-data-labels',
}

/**
 * Wraps a useLocalStorage boolean value with optimistic local state.
 * The toggle updates visually immediately, while the localStorage write
 * (which triggers expensive chart redraws via useSyncExternalStore subscribers)
 * is deferred so it doesn't block the UI.
 */
function useOptimisticToggle(storageValue: boolean, setStorageValue: (v: boolean) => void): [boolean, () => void] {
  const [optimistic, setOptimistic] = useState(storageValue)
  useEffect(() => {
    setOptimistic(storageValue)
  }, [storageValue])
  const toggle = useCallback(() => {
    const next = !optimistic
    setOptimistic(next)
    setTimeout(() => setStorageValue(next), 0)
  }, [optimistic, setStorageValue])
  return [optimistic, toggle]
}

const DialogContentBody: React.FC<{type: 'LineChart' | 'ColumnChart' | 'BaseChart'}> = ({type}) => {
  const [storedColumnDataLabels, setStoredColumnDataLabels] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardColumnDataLabels,
    false,
  )
  const [storedIncreasedContrast, setStoredIncreasedContrast] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardIncreasedContrast,
    false,
  )
  const [storedHasLineStyle, setStoredHasLineStyle] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardHasLineStyle,
    true,
  )
  const [storedLineDataLabels, setStoredLineDataLabels] = useLocalStorage(
    CHART_CARD_CUSTOMIZATION_SETTINGS.chartCardLineDataLabels,
    false,
  )

  const [chartCardColumnDataLabels, toggleColumnDataLabels] = useOptimisticToggle(
    storedColumnDataLabels,
    setStoredColumnDataLabels,
  )
  const [chartCardIncreasedContrast, toggleIncreasedContrast] = useOptimisticToggle(
    storedIncreasedContrast,
    setStoredIncreasedContrast,
  )
  const [chartCardHasLineStyle, toggleHasLineStyle] = useOptimisticToggle(storedHasLineStyle, setStoredHasLineStyle)
  const [chartCardLineDataLabels, toggleLineDataLabels] = useOptimisticToggle(
    storedLineDataLabels,
    setStoredLineDataLabels,
  )
  if (type === 'ColumnChart') {
    return (
      <div className={styles.ChartCardDialogBody}>
        <div className={styles.ChartCardDialogRow}>
          <div className={styles.ChartCardDialogColGrow}>
            <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelIncreaseContrast">
              Increase Contrast
            </h2>
            <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionIncreaseContrast">
              Adds gradients and outlines to increase contrast in charts.
            </span>
          </div>
          <ToggleSwitch
            size="small"
            aria-labelledby="switchLabelIncreaseContrast"
            aria-describedby="switchCaptionIncreaseContrast"
            checked={chartCardIncreasedContrast}
            onClick={toggleIncreasedContrast}
          />
        </div>
        <div className={styles.ChartCardDialogRow}>
          <div className={styles.ChartCardDialogColGrow}>
            <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelColumnDataLabels">
              Show data labels
            </h2>
            <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionColumnDataLabels">
              Show labels for each data point in column and bar charts.
            </span>
          </div>
          <ToggleSwitch
            size="small"
            aria-labelledby="switchLabelColumnDataLabels"
            aria-describedby="switchCaptionColumnDataLabels"
            checked={chartCardColumnDataLabels}
            onClick={toggleColumnDataLabels}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.ChartCardDialogBody}>
      <div className={styles.ChartCardDialogRow}>
        <div className={styles.ChartCardDialogColGrow}>
          <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelLineStyle">
            Differentiate by line style
          </h2>
          <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionLineStyle">
            Use unique line styles (dashed, dotted, etc.) to differentiate lines in charts.
          </span>
        </div>
        <ToggleSwitch
          size="small"
          aria-labelledby="switchLabelLineStyle"
          aria-describedby="switchCaptionLineStyle"
          checked={chartCardHasLineStyle}
          onClick={toggleHasLineStyle}
        />
      </div>
      <div className={styles.ChartCardDialogRow}>
        <div className={styles.ChartCardDialogColGrow}>
          <h2 className={styles.ChartCardDialogSwitchLabel} id="switchLabelLineDataLabels">
            Show data labels
          </h2>
          <span className={styles.ChartCardDialogSwitchCaption} id="switchCaptionLineDataLabels">
            {type === 'LineChart'
              ? 'Show labels for each data point in line chart.'
              : 'Show labels for each data point in chart.'}
          </span>
        </div>
        <ToggleSwitch
          size="small"
          aria-labelledby="switchLabelLineDataLabels"
          aria-describedby="switchCaptionLineDataLabels"
          checked={chartCardLineDataLabels}
          onClick={toggleLineDataLabels}
        />
      </div>
    </div>
  )
}

const CustomizationDialog: React.FC<{chartType?: string; onCloseCallback: () => void; isBaseDialog?: boolean}> = ({
  chartType = '',
  onCloseCallback,
  isBaseDialog = false,
}) => {
  let title = 'Chart settings'
  let dialogContentType: 'BaseChart' | 'ColumnChart' | 'LineChart' = 'BaseChart'
  if (!isBaseDialog) {
    title = COLUMN_BAR_CHART_TYPES.includes(chartType) ? 'Column & bar chart settings' : 'Line chart settings'
    dialogContentType = COLUMN_BAR_CHART_TYPES.includes(chartType) ? 'ColumnChart' : 'LineChart'
  }
  return (
    chartType && (
      <Dialog
        title={title}
        onClose={onCloseCallback}
        renderBody={() => <DialogContentBody type={dialogContentType} />}
      />
    )
  )
}

export default CustomizationDialog
